// =============================================
// File: src/hooks/useRecoveryDatasets.js
// Purpose: Build chart datasets (fit / median line / peaks line & points) + xMax
// =============================================
import { useMemo } from "react";
import { chooseRecoveryModel } from "../utils/exertion/recovery";

export function useRecoveryDatasets({ data, t }) {
  return useMemo(() => {
    const times = Array.isArray(data?.peak_times_s) ? data.peak_times_s : null;
    const values = Array.isArray(data?.peak_values) ? data.peak_values : null;
    const yhat = Array.isArray(data?.decay_fit?.exp?.yhat)
      ? data.decay_fit.exp.yhat
      : null;

    // Median trace
    const medianArr = Array.isArray(data?.median_trace)
      ? data.median_trace
      : null;
    const dur = Number(data?.duration_s_full);
    let medianSeries = null;
    if (medianArr && medianArr.length > 1 && Number.isFinite(dur) && dur > 0) {
      const n = medianArr.length;
      const dt = dur / (n - 1);
      medianSeries = medianArr
        .map((y, i) => ({ x: i * dt, y: Number(y) }))
        .filter((p) => Number.isFinite(p.x) && Number.isFinite(p.y));
    }

    if (!times || !values || !times.length || !values.length) {
      return {
        fitDataset: null,
        medianLineDataset: medianSeries
          ? {
              label: t("activity.exertion.peaksLabel", {
                defaultValue: "Breathing Trace",
              }),
              data: medianSeries,
              borderWidth: 2.5,
              tension: 0.15,
              pointRadius: 0,
            }
          : null,
        peaksLineDataset: null,
        peaksPointsDataset: null,
        xMax: Number.isFinite(dur) ? Math.max(15, Math.ceil(dur)) : 60,
      };
    }

    // Clamp to common length for safety
    const n = Math.min(times.length, values.length);
    const timesN = times.slice(0, n);
    const valuesN = values.slice(0, n);
    const yhatN = Array.isArray(yhat) ? yhat.slice(0, n) : null;

    const peaksSeries = timesN
      .map((x, i) => ({ x: Number(x), y: Number(valuesN[i]) }))
      .filter((p) => Number.isFinite(p.x) && Number.isFinite(p.y));

    const fitSeries = yhatN
      ? timesN
          .map((x, i) => ({ x: Number(x), y: Number(yhatN[i]) }))
          .filter((p) => Number.isFinite(p.x) && Number.isFinite(p.y))
      : null;

    const xmax = Number.isFinite(dur)
      ? Math.max(15, Math.ceil(dur))
      : peaksSeries.length > 0
      ? Math.max(15, Math.ceil(peaksSeries[peaksSeries.length - 1].x))
      : 60;

    const medianLineDataset = medianSeries
      ? {
          label: t("activity.exertion.peaksLabel", {
            defaultValue: "Breathing Trace",
          }),
          data: medianSeries,
          borderWidth: 1,
          tension: 0.15,
          pointRadius: 0,
        }
      : null;

    const peaksLineDataset = {
      label: t("activity.exertion.peaksLabel", {
        defaultValue: "Breathing Trace",
      }),
      data: peaksSeries,
      borderWidth: 2,
      tension: 0,
      pointRadius: 0,
      pointHoverRadius: 0,
      tension: 0.3,
      showLine: true,
    };

    const peaksPointsDataset = {
      label: t("activity.exertion.peaksPointsLabel", {
        defaultValue: "Breathing Peaks",
      }),
      data: peaksSeries,
      pointRadius: 3.5,
      pointHoverRadius: 5,
      borderWidth: 3.5,
      showLine: false,
    };

    // Choose model for recovery curve (supports poly/exponential etc.)
    const chosen = chooseRecoveryModel(data);
    const fitFromChoice = chosen.series.length
      ? {
          label: t("activity.exertion.fitLabel", {
            defaultValue: "Recovery Curve",
          }),
          data: chosen.series,
          borderWidth: 3,
          tension: 0.25,
          pointRadius: 0,
        }
      : null;

    return {
      fitDataset: fitFromChoice,
      medianLineDataset,
      peaksLineDataset,
      peaksPointsDataset,
      xMax: xmax,
    };
  }, [data, t]);
}
